// ============================================================================
// USE TEMPLATES HOOK
// Custom hook for template management operations
// ============================================================================

import { useState, useCallback } from 'react';
import {
  SessionTemplate,
  PhaseTemplate,
  TemplateSnapshot,
  CreateSessionTemplateInput,
  CreatePhaseTemplateInput,
  BrowseTemplatesParams
} from '../types';
import {
  browseSessionTemplates,
  getSessionTemplate,
  createSessionTemplate,
  updateSessionTemplate,
  deleteSessionTemplate,
  previewSessionTemplate,
  browsePhaseTemplates,
  getPhaseTemplate,
  createPhaseTemplate,
  updatePhaseTemplate,
  deletePhaseTemplate,
  submitTemplateForReview
} from '../services/templateService';

interface UseTemplatesResult {
  // State
  loading: boolean;
  error: string | null;
  templates: SessionTemplate[];
  phases: PhaseTemplate[];
  currentTemplate: SessionTemplate | null;
  currentPhase: PhaseTemplate | null;
  templatePreview: { template: SessionTemplate; snapshot: TemplateSnapshot } | null;

  // Session Template Actions
  loadSessionTemplates: (params?: BrowseTemplatesParams) => Promise<void>;
  loadSessionTemplate: (id: string, version?: number) => Promise<void>;
  createNewSessionTemplate: (input: CreateSessionTemplateInput) => Promise<SessionTemplate>;
  updateExistingSessionTemplate: (id: string, updates: Partial<CreateSessionTemplateInput> & { changeNotes?: string }) => Promise<SessionTemplate>;
  deleteSessionTemplateById: (id: string, version: number) => Promise<void>;
  previewTemplate: (id: string, version?: number) => Promise<void>;
  submitSessionTemplateForReview: (id: string, version: number, targetScope: 'ORGANIZATION' | 'SYSTEM', notes?: string) => Promise<void>;

  // Phase Template Actions
  loadPhaseTemplates: (params?: BrowseTemplatesParams) => Promise<void>;
  loadPhaseTemplate: (id: string, version?: number) => Promise<void>;
  createNewPhaseTemplate: (input: CreatePhaseTemplateInput) => Promise<PhaseTemplate>;
  updateExistingPhaseTemplate: (id: string, updates: Partial<CreatePhaseTemplateInput> & { changeNotes?: string }) => Promise<PhaseTemplate>;
  deletePhaseTemplateById: (id: string, version: number) => Promise<void>;
  submitPhaseTemplateForReview: (id: string, version: number, targetScope: 'ORGANIZATION' | 'SYSTEM', notes?: string) => Promise<void>;

  // Utility
  clearError: () => void;
  clearTemplate: () => void;
}

export function useTemplates(): UseTemplatesResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SessionTemplate[]>([]);
  const [phases, setPhases] = useState<PhaseTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<SessionTemplate | null>(null);
  const [currentPhase, setCurrentPhase] = useState<PhaseTemplate | null>(null);
  const [templatePreview, setTemplatePreview] = useState<{ template: SessionTemplate; snapshot: TemplateSnapshot } | null>(null);

  // ============================================================================
  // SESSION TEMPLATE ACTIONS
  // ============================================================================

  const loadSessionTemplates = useCallback(async (params?: BrowseTemplatesParams) => {
    setLoading(true);
    setError(null);

    try {
      const result = await browseSessionTemplates(params);
      setTemplates(result.templates);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessionTemplate = useCallback(async (id: string, version?: number) => {
    setLoading(true);
    setError(null);

    try {
      const template = await getSessionTemplate(id, version);
      setCurrentTemplate(template);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewSessionTemplate = useCallback(async (input: CreateSessionTemplateInput) => {
    setLoading(true);
    setError(null);

    try {
      const template = await createSessionTemplate(input);
      setCurrentTemplate(template);
      return template;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExistingSessionTemplate = useCallback(async (
    id: string,
    updates: Partial<CreateSessionTemplateInput> & { changeNotes?: string }
  ) => {
    setLoading(true);
    setError(null);

    try {
      const template = await updateSessionTemplate(id, updates);
      setCurrentTemplate(template);
      return template;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSessionTemplateById = useCallback(async (id: string, version: number) => {
    setLoading(true);
    setError(null);

    try {
      await deleteSessionTemplate(id, version);
      setCurrentTemplate(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const previewTemplate = useCallback(async (id: string, version?: number) => {
    setLoading(true);
    setError(null);

    try {
      const preview = await previewSessionTemplate(id, version);
      setTemplatePreview(preview);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitSessionTemplateForReview = useCallback(async (
    id: string,
    version: number,
    targetScope: 'ORGANIZATION' | 'SYSTEM',
    notes?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      await submitTemplateForReview('SESSION', id, version, targetScope, notes);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // PHASE TEMPLATE ACTIONS
  // ============================================================================

  const loadPhaseTemplates = useCallback(async (params?: BrowseTemplatesParams) => {
    setLoading(true);
    setError(null);

    try {
      const result = await browsePhaseTemplates(params);
      setPhases(result.templates);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPhaseTemplate = useCallback(async (id: string, version?: number) => {
    setLoading(true);
    setError(null);

    try {
      const phase = await getPhaseTemplate(id, version);
      setCurrentPhase(phase);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewPhaseTemplate = useCallback(async (input: CreatePhaseTemplateInput) => {
    setLoading(true);
    setError(null);

    try {
      const phase = await createPhaseTemplate(input);
      setCurrentPhase(phase);
      return phase;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExistingPhaseTemplate = useCallback(async (
    id: string,
    updates: Partial<CreatePhaseTemplateInput> & { changeNotes?: string }
  ) => {
    setLoading(true);
    setError(null);

    try {
      const phase = await updatePhaseTemplate(id, updates);
      setCurrentPhase(phase);
      return phase;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePhaseTemplateById = useCallback(async (id: string, version: number) => {
    setLoading(true);
    setError(null);

    try {
      await deletePhaseTemplate(id, version);
      setCurrentPhase(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitPhaseTemplateForReview = useCallback(async (
    id: string,
    version: number,
    targetScope: 'ORGANIZATION' | 'SYSTEM',
    notes?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      await submitTemplateForReview('PHASE', id, version, targetScope, notes);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearTemplate = useCallback(() => {
    setCurrentTemplate(null);
    setTemplatePreview(null);
  }, []);

  return {
    // State
    loading,
    error,
    templates,
    phases,
    currentTemplate,
    currentPhase,
    templatePreview,

    // Session Template Actions
    loadSessionTemplates,
    loadSessionTemplate,
    createNewSessionTemplate,
    updateExistingSessionTemplate,
    deleteSessionTemplateById,
    previewTemplate,
    submitSessionTemplateForReview,

    // Phase Template Actions
    loadPhaseTemplates,
    loadPhaseTemplate,
    createNewPhaseTemplate,
    updateExistingPhaseTemplate,
    deletePhaseTemplateById,
    submitPhaseTemplateForReview,

    // Utility
    clearError,
    clearTemplate
  };
}
